import { z } from 'zod'
import { optionalHexColorSchema } from '../validators/hex-color.validator.js'

/**
 * A single drawing primitive in a furniture item's local coordinate space.
 *
 * Furniture no longer has fixed "kinds" (round table, chair, …) baked into the
 * renderer. Instead, a furniture item — and every placement drawn from it — owns
 * a free-form list of these primitives that a 2D editor produces and the canvas
 * renders. Each primitive is positioned by its **center** (`x`/`y`) in the item's
 * local space (origin = the item's anchor, which becomes the placement's x/y),
 * so rotation and placement behave uniformly regardless of shape.
 *
 * `fill`/`stroke`/`strokeWidth` are optional; the renderer applies the standard
 * white-fill / gray-stroke venue look when they are absent.
 */
const PRIMITIVE_SHAPES = ['rect', 'circle', 'ellipse', 'wedge', 'arc', 'line', 'text'] as const

export const primitiveShapeSchema = z.enum(PRIMITIVE_SHAPES)

export type PrimitiveShape = z.infer<typeof primitiveShapeSchema>

const base = {
  id: z.string(),
  /** Center of the primitive, in the item's local coordinate space. */
  x: z.number(),
  y: z.number(),
  rotation: z.number().default(0),
  fill: optionalHexColorSchema,
  stroke: optionalHexColorSchema,
  strokeWidth: z.number().optional(),
}

export const primitiveSchema = z.discriminatedUnion('shape', [
  z.object({ ...base, shape: z.literal('rect'), width: z.number(), height: z.number(), cornerRadius: z.number().optional() }),
  z.object({ ...base, shape: z.literal('circle'), radius: z.number() }),
  z.object({ ...base, shape: z.literal('ellipse'), radiusX: z.number(), radiusY: z.number() }),
  // A pie slice, e.g. a half-moon table (angle 180). Drawn centered on x/y.
  z.object({ ...base, shape: z.literal('wedge'), radius: z.number(), angle: z.number() }),
  // A ring segment, e.g. the seating arc on a half-moon table.
  z.object({ ...base, shape: z.literal('arc'), innerRadius: z.number(), outerRadius: z.number(), angle: z.number() }),
  // A polyline / polygon. `points` are [x0,y0,x1,y1,…] relative to x/y.
  z.object({ ...base, shape: z.literal('line'), points: z.array(z.number()), closed: z.boolean().optional() }),
  z.object({ ...base, shape: z.literal('text'), text: z.string(), fontSize: z.number().positive() }),
])

export type Primitive = z.infer<typeof primitiveSchema>
